import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA as MAT_DIALOG_DATA,
  MatDialogRef as MatDialogRef,
  MatDialogModule,
} from '@angular/material/dialog';
import { CONTENT_SLUG, ToastService, UserService } from '../../services';
import { ENV, Environment } from '../../types';
import { Platform } from '@jump-tools-front/shared-interfaces';
import { SupportTicketService } from './support-ticket.service';
import { ModalSupportData, SupportType } from './support.model';
import { first } from 'rxjs/operators';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

import { MatButtonModule } from '@angular/material/button';
import { ButtonLauncherReadSpeakerComponent } from '../read-speaker/button-launcher-read-speaker/button-launcher-read-speaker.component';

const FILE_LIMIT = 20000000;

@Component({
  selector: 'shared-support-modal',
  templateUrl: './support-modal.component.html',
  styleUrls: ['./support-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    ButtonLauncherReadSpeakerComponent,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
  ],
})
export class SupportModalComponent implements OnInit {
  private _dialogRef = inject<MatDialogRef<SupportModalComponent>>(MatDialogRef<SupportModalComponent>);
  private _fb = inject(UntypedFormBuilder);
  private _userService = inject(UserService);
  private _supportTicketService = inject(SupportTicketService);
  private _toastService = inject(ToastService);
  private _cdr = inject(ChangeDetectorRef);
  public modalData = inject<ModalSupportData>(MAT_DIALOG_DATA);
  private _environment = inject<Environment>(ENV);
  errorMessage: string;
  support = this.modalData.data;
  supportType: SupportType = this.modalData.supportType;
  supportTypes = SupportType;

  loader = false;

  formSupport = this._fb.group({
    mail: ['', Validators.required],
    title: ['', Validators.required],
    message: ['', [Validators.required, Validators.maxLength(1000)]],
  });
  fileUploaded: File | null = null;
  showErrorFileLimit = false;
  environment: string;

  @ViewChild('uploader') uploader!: HTMLInputElement;

  constructor() {
    this._dialogRef.addPanelClass(['modal-support', 'modal-medium']);
  }

  ngOnInit(): void {
    this._userService
      .getCurrentUser$()
      .pipe(first())
      .subscribe(user => this.formSupport.controls.mail.setValue(user?.email || ''));
    if (this._environment.platform === Platform.BACK_OFFICE) {
      this.environment = 'backoffice';
    } else if (this._environment.platform === Platform.FRONT_DYNAMIC) {
      this.environment = 'frontdynamic';
    } else if (this._environment.platform === Platform.LEARNIZ) {
      this.environment = 'learniz';
    }
  }

  close(): void {
    this._dialogRef.close();
  }

  sendTicket(): void {
    const { title, message, mail } = this.formSupport.value;
    const productSlug = this._environment.platform === Platform.LEARNIZ ? localStorage.getItem(CONTENT_SLUG) : null;
    this.loader = true;
    this._supportTicketService
      .sendTicket$(title, message, mail, this.supportType, productSlug, this.fileUploaded)
      .subscribe({
        next: () => {
          this._toastService.toastSuccess(this.support['support.success']);
          this._dialogRef.close();
          this.loader = false;
        },
        error: () => (this.errorMessage = this.support['support.fail']),
      });
  }

  addFile(event: Event): void {
    const input = <HTMLInputElement>event.target;
    const fileList: FileList = input.files;
    this.showErrorFileLimit = false;
    if (fileList?.[0].size > FILE_LIMIT) {
      this.showErrorFileLimit = true;
      this.clearFiles();
      return;
    }
    this.fileUploaded = fileList?.[0];
    this._cdr.markForCheck();
  }

  clearFiles(): void {
    this.fileUploaded = null;
    this._cdr.markForCheck();
  }
}
